// Task detail (§11 `/tasks/:id`): config, schedule preview, recent runs, and lifecycle actions
// (pause/resume/run-now/delete/restore). Mutations carry If-Match: version (optimistic concurrency,
// invariant #9); a 412 surfaces a conflict banner prompting a reload.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Api, ApiError, type Task } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtRelative, fmtTime, fmtDuration } from "../lib/format";
import { Badge, Card, Empty, ErrorBox, Spinner, StatusBadge } from "../components/ui";

export function TaskDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const task = useAsync(() => Api.getTask(id), [id]);
  const runs = useAsync(() => Api.listRuns({ taskId: id, limit: "20" }), [id]);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);

  async function act(fn: (t: Task) => Promise<unknown>) {
    if (!task.data) return;
    setActionError(null);
    setBusy(true);
    try {
      await fn(task.data);
      task.reload();
      runs.reload();
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        setActionError(new Error("Conflict: this task changed since you loaded it. Reload and retry."));
      } else {
        setActionError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setBusy(false);
    }
  }

  if (task.loading && !task.data) return <Spinner />;
  if (task.error) return <ErrorBox error={task.error} />;
  if (!task.data) return null;
  const t = task.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-mono text-xl font-semibold text-white">{t.id}</h1>
          <div className="text-sm text-muted">
            <Link className="text-primary hover:underline" to={`/services/${t.serviceId}`}>
              {t.serviceId}
            </Link>{" "}
            · v{t.version}
          </div>
        </div>
        <Link className="btn" to="/tasks">
          Back
        </Link>
      </div>

      {actionError && <ErrorBox error={actionError} />}

      <div className="flex flex-wrap gap-2">
        <button className="btn" disabled={busy} onClick={() => act((x) => Api.runNow(x.id))}>
          Run now
        </button>
        {t.targetType === "group" && (
          <Link className="btn" to={`/tasks/${t.id}/fanout`}>
            Fan-out
          </Link>
        )}
        {t.paused ? (
          <button className="btn" disabled={busy} onClick={() => act((x) => Api.resumeTask(x.id, x.version))}>
            Resume
          </button>
        ) : (
          <button className="btn" disabled={busy} onClick={() => act((x) => Api.pauseTask(x.id, x.version))}>
            Pause
          </button>
        )}
        {t.deletedAt ? (
          <button className="btn" disabled={busy} onClick={() => act((x) => Api.restoreTask(x.id))}>
            Restore
          </button>
        ) : (
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={() => {
              if (confirm(`Soft-delete task ${t.id}?`)) {
                act(async (x) => {
                  await Api.deleteTask(x.id, x.version);
                  navigate("/tasks");
                });
              }
            }}
          >
            Delete
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Configuration">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-muted">Target</dt>
            <dd>
              <Badge>{t.targetType}</Badge>{" "}
              {t.targetType === "http" && <span className="mono text-xs text-muted">{t.method} {t.path}</span>}
              {t.targetType === "group" && <span className="mono text-xs text-muted">{t.targetGroupId}</span>}
              {t.targetType === "workflow" && <span className="mono text-xs text-muted">{t.workflowBinding}</span>}
            </dd>
            <dt className="text-muted">Schedule</dt>
            <dd className="mono text-xs">
              {t.scheduleKind === "cron"
                ? t.cron
                : t.scheduleKind === "interval"
                  ? `every ${t.intervalSeconds}s`
                  : "one-shot"}
            </dd>
            <dt className="text-muted">Engine</dt>
            <dd>
              <Badge tone={t.scheduleEngine === "do_alarm" ? "warn" : "muted"}>{t.scheduleEngine}</Badge>
            </dd>
            <dt className="text-muted">Timezone</dt>
            <dd className="mono text-xs">{t.timezone}</dd>
            <dt className="text-muted">Next run</dt>
            <dd className="mono text-xs">
              {fmtTime(t.nextRunAt)} <span className="text-muted">({fmtRelative(t.nextRunAt)})</span>
            </dd>
            <dt className="text-muted">State</dt>
            <dd>
              {t.deletedAt ? (
                <Badge tone="danger">deleted</Badge>
              ) : t.paused ? (
                <Badge tone="warn">paused</Badge>
              ) : t.enabled ? (
                <Badge tone="ok">enabled</Badge>
              ) : (
                <Badge tone="danger">disabled</Badge>
              )}
            </dd>
          </dl>
        </Card>

        <Card title="Retry & flow control">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-muted">Max attempts</dt>
            <dd className="mono">{t.maxAttempts}</dd>
            <dt className="text-muted">Backoff base</dt>
            <dd className="mono">{t.backoffBaseSeconds}s ×{t.backoffFactor}</dd>
            <dt className="text-muted">Max backoff</dt>
            <dd className="mono">{t.maxBackoffSeconds}s</dd>
            <dt className="text-muted">Timeout</dt>
            <dd className="mono">{t.timeoutMs ?? "service default"}</dd>
            <dt className="text-muted">Max RPS</dt>
            <dd className="mono">{t.maxRps ?? "—"}</dd>
            <dt className="text-muted">Max parallelism</dt>
            <dd className="mono">{t.maxParallelism ?? "—"}</dd>
            <dt className="text-muted">Dedup window</dt>
            <dd className="mono">{t.dedupWindowSeconds != null ? `${t.dedupWindowSeconds}s` : "—"}</dd>
            <dt className="text-muted">Overlap policy</dt>
            <dd className="mono">{t.overlapPolicy}</dd>
          </dl>
        </Card>
      </div>

      {t.payload != null && (
        <Card title="Payload">
          <pre className="overflow-x-auto rounded bg-bg p-3 font-mono text-xs text-slate-300">
            {JSON.stringify(t.payload, null, 2)}
          </pre>
        </Card>
      )}

      <Card title="Recent runs">
        {runs.loading && !runs.data ? (
          <Spinner />
        ) : runs.error ? (
          <ErrorBox error={runs.error} />
        ) : !runs.data || runs.data.data.length === 0 ? (
          <Empty>No runs yet.</Empty>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Run</th>
                <th className="th">Slot</th>
                <th className="th">Attempt</th>
                <th className="th">Status</th>
                <th className="th">HTTP</th>
                <th className="th">Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.data.map((r) => (
                <tr key={r.id}>
                  <td className="td">
                    <Link className="mono text-xs text-primary hover:underline" to={`/runs/${r.id}`}>
                      {r.id}
                    </Link>
                  </td>
                  <td className="td mono text-xs">{fmtTime(r.scheduledSlot)}</td>
                  <td className="td mono text-xs">{r.attempt}</td>
                  <td className="td">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="td mono text-xs">{r.responseStatus ?? "—"}</td>
                  <td className="td mono text-xs">{fmtDuration(r.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}